import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { callAI } from "./aiClient";
import { useTheme } from "./theme";

export default function AIAssistant() {
  const C = useTheme();
  const { t } = useTranslation();

  const SUGGESTIONS = [
    t('ai.suggestions.reviewPolicy'),
    t('ai.suggestions.draftEdd'),
    t('ai.suggestions.sarTriggers'),
    t('ai.suggestions.riskBased'),
  ];

  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "Hello! I'm your AML compliance AI assistant. I can help you draft and review policies, answer regulatory questions, and align with FATF, FinCEN, and FCA guidelines. What would you like to work on?",
  }]);
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const endRef                = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  async function send() {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const reply = await callAI({
        system: "You are an expert AML compliance AI assistant. Help compliance officers with policies, regulations (FATF, FinCEN, FCA), KYC/CDD/EDD, SARs, and best practices. Be precise and professional.",
        messages: messages.concat({ role: "user", content: userMsg }),
        maxTokens: 1000,
      });
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: `Error: ${err.message}` }]);
    }
    setLoading(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>{t('ai.title')}</h1>
        <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>{t('ai.subtitle')}</p>
      </div>

      <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Message list */}
        <div style={{ flex: 1, overflowY: "auto", padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "78%", padding: "11px 15px", borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: m.role === "user" ? `linear-gradient(135deg, ${C.accent}, ${C.accentDim})` : C.bg, border: m.role === "assistant" ? `1px solid ${C.border}` : "none", color: m.role === "user" ? C.bg : C.text, fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {m.role === "assistant" && <div style={{ color: C.accent, fontSize: 11, fontFamily: "'DM Mono', monospace", marginBottom: 7 }}>{t('ai.assistantLabel')}</div>}
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", gap: 6 }}>
              {[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, animation: `crg-pulse 1.2s ${i * 0.2}s infinite` }} />)}
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Suggestion chips — shown only on fresh chat */}
        {messages.length <= 1 && (
          <div style={{ padding: "0 22px 14px", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {SUGGESTIONS.map(s => (
              <button key={s} onClick={() => setInput(s)} style={{ padding: "5px 12px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 20, color: C.accent, fontSize: 12, cursor: "pointer", fontFamily: "'DM Mono', monospace" }}>{s}</button>
            ))}
          </div>
        )}

        {/* Input bar */}
        <div style={{ borderTop: `1px solid ${C.border}`, padding: 14, display: "flex", gap: 10 }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t('ai.inputPlaceholder')}
            rows={1}
            style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 14, padding: "9px 12px", fontFamily: "'DM Mono', monospace", resize: "none", outline: "none", minHeight: 42, maxHeight: 120, lineHeight: 1.5 }}
          />
          <button onClick={send} disabled={loading || !input.trim()} style={{ padding: "9px 18px", background: input.trim() ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
            {t('ai.send')}
          </button>
        </div>
      </div>

      <style>{`@keyframes crg-pulse { 0%,80%,100%{opacity:0.3;transform:scale(0.8)} 40%{opacity:1;transform:scale(1)} }`}</style>
    </div>
  );
}